import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import { useState, useEffect, useCallback } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import useMonthNames from "@/hooks/useMonthNames";
import useYearMonthDates from "@/hooks/useYearMonthDates";
import { useYearRange } from "@/hooks/useYearRange";
import { isMonthOutOfRange } from "@/lib/utils/isMonthOutOfRange";

interface MonthPickerProps {
  value?: Date;
  /**
   * The start year offset of the calendar. Defaults to the current year minus 5.
   */
  startYearOffset?: number;
  /**
   * The end year offset of the calendar. Defaults to the current year plus 5.
   */
  endYearOffset?: number;
  onChange?: (date: Date) => void;
  disabled?: boolean;
  selectableFromDate?: Date;
  selectableToDate?: Date;
}

function MonthPicker({
  value,
  startYearOffset = 5,
  endYearOffset = 5,
  onChange,
  disabled,
  selectableFromDate,
  selectableToDate,
}: MonthPickerProps) {
  const { t } = useTranslation();
  const [currentYear, setCurrentYear] = useState<number>(
    (value || new Date()).getFullYear(),
  );
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(value);
  const months = useMonthNames();
  const dates = useYearMonthDates(currentYear);
  const isMonthDisabled = useCallback(
    (date: Date): boolean =>
      isMonthOutOfRange(date, selectableFromDate, selectableToDate),
    [selectableFromDate, selectableToDate],
  );

  useEffect(() => {
    setSelectedDate(value);
    if (value) {
      setCurrentYear(value.getFullYear());
    }
  }, [value]);

  // Clear selection when the selectable range changes and the currently selected month is no longer
  // allowed.
  useEffect(() => {
    if (selectedDate && isMonthDisabled(selectedDate)) {
      setSelectedDate(undefined);
    }
  }, [isMonthDisabled, selectedDate]);

  const handleMonthSelect = (monthIndex: number) => {
    const newDate = new Date(currentYear, monthIndex, 1);
    setSelectedDate(newDate);
    onChange?.(newDate);
  };

  const handlePreviousYear = useCallback(() => {
    setCurrentYear(currentYear - 1);
  }, [currentYear]);

  const handleNextYear = useCallback(() => {
    setCurrentYear(currentYear + 1);
  }, [currentYear]);

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const year = parseInt(e.target.value, 10);
    setCurrentYear(year);
  };

  const years = useYearRange(startYearOffset, endYearOffset);
  const minYear = years[0];
  const maxYear = years[years.length - 1];

  const isMonthSelected = useCallback(
    (date: Date) => {
      if (isMonthDisabled(date)) {
        return false;
      }
      return (
        selectedDate &&
        selectedDate.getMonth() === date.getMonth() &&
        selectedDate.getFullYear() === date.getFullYear()
      );
    },
    [isMonthDisabled, selectedDate],
  );

  return (
    <div className="w-full p-3 bg-white rounded-md shadow-sm border border-border">
      <div className="flex items-center justify-between mb-4">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
          onClick={handlePreviousYear}
          disabled={disabled || currentYear <= minYear}
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="sr-only">{t("year.previous")}</span>
        </Button>

        <select
          value={currentYear}
          onChange={handleYearChange}
          className="bg-transparent text-center text-sm font-medium p-1 rounded outline-none cursor-pointer focus:ring-1 focus:ring-primary"
          disabled={disabled}
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
          onClick={handleNextYear}
          disabled={disabled || currentYear >= maxYear}
        >
          <ChevronRight className="h-4 w-4" />
          <span className="sr-only">{t("year.next")}</span>
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {dates.map((date, index) => (
          <Button
            key={date.toString()}
            type="button"
            onClick={() => handleMonthSelect(index)}
            variant={isMonthSelected(date) ? "default" : "outline"}
            disabled={disabled || isMonthDisabled(date)}
            className={cn(
              "h-10 capitalize",
              isMonthSelected(date) && "text-primary-foreground",
            )}
          >
            {months[index]}
          </Button>
        ))}
      </div>
    </div>
  );
}

export { MonthPicker };
